'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { PortableText } from '@/lib/portableText';
import type { Author } from '@/studio/sanity.types';
import getImage from '@/utils/getImageData';

import { InstagramIcon } from '../icons/InstagramIcon';
import { LInkedinIcon } from '../icons/LinkedinIcon';
import SocialIcon from '../icons/SocialIcon';
import { TwitterIcon } from '../icons/TwitterIcon';
import GradientHeading from '../shared/gradientHeading';

export default function AuthorCard({ author }: { author: Author }) {
  const [isHovered, setIsHovered] = useState(false);
  const imageProps = getImage(author.image, { width: 400, height: 400, quality: 90 });

  const socialIcons = [
    { icon: 'twitter', url: '/' },
    { icon: 'linkedin', url: '/' },
    { icon: 'instagram', url: '/' },
  ].filter(social => social.url !== '#');

  return (
    <div
      className="relative mt-6 overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-gray-800"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 opacity-5">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
              <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative z-10 flex flex-col items-center p-6 sm:flex-row sm:items-start sm:space-x-6">
        <div className="group relative mb-4 h-28 w-28 flex-shrink-0 sm:mb-0">
          <div
            className={`absolute -inset-1 rounded-full bg-gradient-to-br from-green-600 to-blue-600 opacity-70 blur transition-all duration-500 dark:from-purple-600 dark:to-blue-600 ${isHovered ? 'opacity-100' : 'opacity-70'}`}
          />

          <div className="relative h-28 w-28 overflow-hidden rounded-full border-2 border-white bg-white dark:border-gray-700">
            {imageProps ? (
              <Image
                unoptimized
                src={imageProps.src}
                blurDataURL={imageProps.blurDataURL}
                alt={'AllBlogs Author: ' + author.name}
                title={'AllBlogs Author: ' + author.name}
                placeholder="blur"
                fill
                className="rounded-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900">
                <span className="text-2xl font-bold text-blue-500">{author.name?.charAt(0)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1">
          <div className="text-center sm:text-left">
            <GradientHeading level={3} className="mb-2 text-xl font-bold">
              {author.name}
            </GradientHeading>
          </div>

          <div className="prose-sm prose dark:prose-invert mt-3 line-clamp-4 max-w-none text-gray-600 dark:text-gray-300">
            {author.bio && <PortableText value={author.bio} />}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 sm:justify-between">
            <Link
              prefetch={false}
              href={`/author/${author?.slug.current}`}
              className={`inline-flex items-center rounded-lg bg-gradient-to-r from-green-600 to-blue-600 px-5 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:to-blue-700 hover:shadow-lg dark:from-purple-600 dark:to-blue-600 ${isHovered ? 'scale-105' : ''}`}
            >
              View Full Profile
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1.5 h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>

            {/* Social media icons */}
            {socialIcons.length > 0 && (
              <div className="flex gap-2">
                {socialIcons.map(social => renderSocialIcon(social.icon, social.url))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to render social icons
function renderSocialIcon(platform: string, url: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const iconMap: Record<string, any> = {
    twitter: <TwitterIcon />,
    linkedin: <LInkedinIcon />,
    instagram: <InstagramIcon />,
  };

  return (
    <SocialIcon
      color={''}
      title={platform}
      href={url}
      key={platform}
      className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-green-600 to-blue-600 text-white transition-all dark:from-purple-500 dark:to-indigo-600"
    >
      {iconMap[platform] || (
        <span className="text-xs font-medium">{platform.charAt(0).toUpperCase()}</span>
      )}
    </SocialIcon>
  );
}
